import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { IconUserPlus, IconAlertTriangle } from "@tabler/icons-react";
import styles from "./teamManagement.module.css";
import Button from "../../../components/UI/Button/button";
import Breadcrumb from "../../../components/UI/Breadcrumb/Breadcrumb";
import TeamHero from "../../../components/TeamManagement/TeamHero";
import UserManagementCard from "../../../components/TeamManagement/UserManagementCard";
import TeamManagementCard from "../../../components/TeamManagement/TeamManagementCard";
import RoleBreakdownCard from "../../../components/TeamManagement/RowBreakdownCard";
import QuickActionsCard from "../../../components/TeamManagement/QuickActionCard";
import RolePermissionsCard from "../../../components/TeamManagement/RolePermissionCard";
import AddUserModal from "../../../components/TeamManagement/AddUserModal";
import AddTeamModal from "../../../components/TeamManagement/AddTeamModal";
import EditUserModal from "../../../components/TeamManagement/EditUserModal";
import EditTeamModal from "../../../components/TeamManagement/EditTeamModal";
import {
  clientTeamGetTeams,
  clientTeamInviteUser,
  clientTeamResendInvite,
  clientTeamEditUser,
  clientTeamRemoveUser,
  clientTeamCreateTeam,
  clientTeamEditTeam,
  clientTeamDeleteTeam,
  clientTeamGetUsers,
} from "../../../services/clientCalls";
import { errorHandler, toastSuccess } from "../../../utils";
import { getTeamApiError } from "../../../components/TeamManagement/utils/apiError";
const MANAGER_ALLOWED_ROLES = ["Member", "Viewer"];

const TeamManagement = () => {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTeams: 0,
    admins: 0,
    pending: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    pages: 1,
  });

  const [usersLoading, setUsersLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [teamsError, setTeamsError] = useState(null);

  const [filters, setFilters] = useState({ search: "", role: "", teamId: "" });

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addTeamOpen, setAddTeamOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingTeamId, setEditingTeamId] = useState(null);

  const editingUser = useMemo(
    () => users?.find((u) => u?._id === editingUserId) || null,
    [users, editingUserId],
  );
  const editingTeam = useMemo(
    () => teams?.find((t) => t?._id === editingTeamId) || null,
    [teams, editingTeamId],
  );

  const currentUserRole = localStorage.getItem("role") || "";
  const currentUserEmail = (localStorage.getItem("email") || "").toLowerCase();
  const currentUser = useMemo(
    () =>
      users?.find((u) => u?.email?.toLowerCase() === currentUserEmail) || null,
    [users, currentUserEmail],
  );
  const currentUserId = currentUser?._id || null;

  const canWrite = currentUserRole === "client" || currentUserRole === "Manager";
  const isAdmin = currentUserRole === "client";

  /** Whether the current user is allowed to invite/edit/remove `targetRole`. */
  const canManageRole = useCallback(
    (targetRole) => {
      if (!canWrite) return false;
      if (isAdmin) return true;
      return MANAGER_ALLOWED_ROLES.includes(targetRole);
    },
    [canWrite, isAdmin],
  );

  // ── Fetching ── //
  const fetchUsers = useCallback(
    async (overrides = {}) => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const params = {
          search: filters.search || undefined,
          role: filters.role || undefined,
          teamId: filters.teamId || undefined,
          page: 1,
          limit: 100,
          ...overrides,
        };
        const res = await clientTeamGetUsers(params);
        if (res?.data?.data?.users) {
          setUsers(res?.data?.data?.users);
        }
        if (res?.data?.data?.pagination) {
          setPagination(res?.data?.data?.pagination);
        }
        if (res?.data?.data?.stats) {
          setStats(res?.data?.data?.stats);
        }
      } catch (err) {
        setUsersError(getTeamApiError(err));
      } finally {
        setUsersLoading(false);
      }
    },
    [filters.search, filters.role, filters.teamId],
  );

  const fetchTeams = useCallback(async () => {
    setTeamsLoading(true);
    setTeamsError(null);
    try {
      const res = await clientTeamGetTeams();
      if (res?.data?.data?.teams) {
        setTeams(res?.data?.data?.teams);
      }
    } catch (err) {
      setTeamsError(getTeamApiError(err));
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  // Initial load.
  useEffect(() => {
    fetchUsers();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const t = setTimeout(() => fetchUsers(), 350);
    return () => clearTimeout(t);
  }, [filters.search, filters.role, filters.teamId, fetchUsers]);


  const refetchAll = useCallback(() => {
    fetchUsers();
    fetchTeams();
  }, [fetchUsers, fetchTeams]);

  const handleAddUser = async (payload) => {
    await clientTeamInviteUser(payload);
    setAddUserOpen(false);
    toastSuccess("Invite sent.");
    refetchAll();
  }

  const handleEditUser = (id) => {
    setEditingUserId(id);
  }

  const handleSaveEditUser = async (id, changedFields) => {
    if (Object.keys(changedFields).length === 0) {
      setEditingUserId(null);
      return;
    }
    const res = await clientTeamEditUser(id, changedFields);
    setEditingUserId(null);
    const wasPendingEmailChange =
      editingUser?.status === "pending" && "email" in changedFields;
    toastSuccess(
      wasPendingEmailChange
        ? "User updated — invite re-sent to the new address."
        : "User updated.",
    );
    refetchAll();
    return res;
  }

  const handleResendInvite = async (id) => {
    try {
      await clientTeamResendInvite(id);
      toastSuccess("Invite resent.");
    } catch (err) {
      errorHandler(getTeamApiError(err), "");
    }
  }

  const handleRemoveUser = async (id) => {
    const target = users?.find((u) => u?._id === id);
    if (!target) return;
    if (!window.confirm(`Remove ${target?.name} from your organisation?`))
      return;
    try {
      await clientTeamRemoveUser(id);
      toastSuccess("User removed.");
      refetchAll();
    } catch (err) {
      errorHandler(getTeamApiError(err), "");
    }
  }

  // ── Team handlers ── //
  const handleAddTeam = async (payload) => {
    await clientTeamCreateTeam(payload);
    setAddTeamOpen(false);
    toastSuccess("Team created.");
    refetchAll();
  }

  const handleEditTeam = (id) => {
    setEditingTeamId(id);
  }

  const handleSaveEditTeam = async (id, changedFields) => {
    if (Object.keys(changedFields).length === 0) {
      setEditingTeamId(null);
      return;
    }
    const res = await clientTeamEditTeam(id, changedFields);
    setEditingTeamId(null);
    toastSuccess("Team updated.");
    refetchAll();
    return res;
  }

  const handleDeleteTeam = async (id) => {
    const target = teams?.find((t) => t?._id === id);
    if (!target) return;
    if (
      !window.confirm(
        `Delete team "${target?.name}"? Members will not be removed.`,
      )
    )
      return;
    try {
      await clientTeamDeleteTeam(id);
      toastSuccess("Team deleted.");
      refetchAll();
    } catch (err) {
      errorHandler(getTeamApiError(err), "");
    }
  }

  const initialLoading =
    usersLoading && teamsLoading && users.length === 0 && teams.length === 0;

  return (
    <>
      <Breadcrumb
        crumbs={[
          { label: "Dashboard", href: "/client-activity-summary" },
          { label: "Team Management" },
        ]}
        title="Team & User Management"
        actions={
          canWrite && (
            <Button
              variant="primary"
              leftIcon={<IconUserPlus size={16} />}
              onClick={() => setAddUserOpen(true)}
            >
              Add User
            </Button>
          )
        }
      />

      <TeamHero
        totalUsers={stats.totalUsers}
        totalTeams={stats.totalTeams}
        admins={stats.admins}
        pending={stats.pending}
      />

      {(usersError || teamsError) && (
        <div className={styles.apiErrorBanner}>
          <IconAlertTriangle size={16} />
          <span>{usersError || teamsError}</span>
        </div>
      )}

      <div className={styles.tmGrid}>
        <div className={styles.tmMain}>
          <UserManagementCard
            users={users}
            teams={teams}
            loading={usersLoading}
            filters={filters}
            onFilterChange={(next) => setFilters((f) => ({ ...f, ...next }))}
            currentUserId={currentUserId}
            canManageRole={canManageRole}
            onEditUser={handleEditUser}
            onRemoveUser={handleRemoveUser}
            onResendInvite={handleResendInvite}
            pagination={pagination}
          />
          <TeamManagementCard
            teams={teams}
            users={users}
            loading={teamsLoading}
            canWrite={canWrite}
            onAddTeam={() => setAddTeamOpen(true)}
            onEditTeam={handleEditTeam}
            onDeleteTeam={handleDeleteTeam}
          />
        </div>

        <div className={styles.tmSidebar}>
          <RoleBreakdownCard users={users} totalAdmins={stats.admins} />
          <QuickActionsCard
            canWrite={canWrite}
            onInviteUser={() => setAddUserOpen(true)}
            onCreateTeam={() => setAddTeamOpen(true)}
          />
          <RolePermissionsCard />
        </div>
      </div>

      <AddUserModal
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onAddUser={handleAddUser}
        teams={teams}
        currentUserRole={currentUserRole}
      />
      <AddTeamModal
        open={addTeamOpen}
        onClose={() => setAddTeamOpen(false)}
        onAddTeam={handleAddTeam}
        users={users}
      />
      <EditUserModal
        open={editingUserId !== null}
        user={editingUser}
        teams={teams}
        currentUserRole={currentUserRole}
        currentUserId={currentUserId}
        onClose={() => setEditingUserId(null)}
        onSave={handleSaveEditUser}
      />
      <EditTeamModal
        open={editingTeamId !== null}
        team={editingTeam}
        users={users}
        onClose={() => setEditingTeamId(null)}
        onSave={handleSaveEditTeam}
      />
    </>
  );
};

export default TeamManagement;
